import React, { FC, useContext, useState } from "react";
import { styled } from "@mui/material/styles";
import { AddToProjectDialog } from "./CaseForms/AddToProjectDialog";
import { Case } from "interfaces/case";
import {
  IAddCasesToProject,
  IProject,
  IProjectsResponse
} from "interfaces/project";
import { Autocomplete } from "components/Autocomplete";
import { API_URL_PROJECTS } from "constants/api_urls";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosContext } from "contexts/AxiosContext";
import { makeQueryString } from "core/makeQueryString";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";

interface AddToProjectDropdownProps {
  disabled?: boolean;
  selectedCases: Case[];
  onProjectAdded: () => void;
}

const DEFAULT_SORTING = "name";

export const AddToProjectDropdown: FC<AddToProjectDropdownProps> = ({
  disabled,
  selectedCases,
  onProjectAdded
}) => {
  const [selectedProject, setSelectedProject] = useState<IProject | null>(null);
  const [addToProjectDialogVisible, setAddToProjectDialogVisible] = useState(
    false
  );

  const { axios } = useContext(AxiosContext)!;
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const fetchProjectsQueryString = makeQueryString({
    ordering: DEFAULT_SORTING
  });

  const fetchProjects = useQuery({
    queryKey: ["projects"],
    queryFn: () =>
      axios
        .get<IProjectsResponse>(
          `${API_URL_PROJECTS}${fetchProjectsQueryString}`
        )
        .then(r => r.data),
    cacheTime: 0,
    refetchOnWindowFocus: false
  });

  const handleSelectProject = (value: IProject): void => {
    setSelectedProject(value);
    setAddToProjectDialogVisible(true);
  };

  const postCasesToProject = useMutation({
    mutationFn: (projectCases: IAddCasesToProject) =>
      axios
        .patch(`projects/${projectCases.project}/add_cases`, {
          cases: projectCases.cases
        })
        .then(response => response.data),
    onSuccess: () => {
      addSnackbarToQueue({
        message: "Case(s) successfully added to Project.",
        severity: "success"
      });
      onProjectAdded();
      setAddToProjectDialogVisible(false);
    },
    onError: () =>
      addSnackbarToQueue({
        message: "Failed to add Case(s) to Project.",
        severity: "error"
      })
  });

  return (
    <>
      <StyledAutocomplete
        label="Add Cases to Project"
        disabled={disabled || !fetchProjects.data?.results}
        loading={fetchProjects.isLoading || postCasesToProject.isLoading}
        error={fetchProjects.error}
        size="small"
        autoHighlight
        disableCloseOnSelect
        blurOnSelect
        clearOnBlur
        disableClearable
        autoComplete
        options={fetchProjects.data?.results || []}
        noOptionsText="No results found"
        getOptionLabel={(option: IProject) => option.name}
        isOptionEqualToValue={(option: IProject, value: IProject) =>
          option.name === value.name
        }
        onChange={(_event: React.ChangeEvent, value: IProject) => {
          handleSelectProject(value);
        }}
        value={selectedProject}
        placeholder="Select a project"
      />
      {addToProjectDialogVisible ? (
        <AddToProjectDialog
          isDialogOpen={addToProjectDialogVisible}
          onConfirm={() => {
            if (selectedProject && selectedProject.id) {
              postCasesToProject.mutateAsync({
                project: selectedProject.id,
                cases: selectedCases.map(selectedCase => selectedCase.id)
              });
              setAddToProjectDialogVisible(false);
              setSelectedProject(null);
            }
          }}
          onClose={() => {
            setAddToProjectDialogVisible(false);
            setSelectedProject(null);
          }}
          selectedCases={selectedCases}
          selectedProjectId={selectedProject?.id ?? null}
        />
      ) : null}
    </>
  );
};

const StyledAutocomplete = styled(Autocomplete)`
  width: 200px;
`;
